import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentsService } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const INITIAL_FORM = {
  interviewee_name: '',
  interviewee_role: '',
  interview_date: '',
  transcript: ''
};

const InterviewUpload = ({ complaintId, canEdit = true, onRefresh }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agentsService.getInterviews(complaintId);
      setInterviews(response.interviews || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar entrevistas.');
    } finally {
      setLoading(false);
    }
  }, [complaintId]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.interviewee_name.trim() || !form.transcript.trim()) {
      toast.warning('Nome do entrevistado e transcrição são obrigatórios.');
      return;
    }

    try {
      setSubmitting(true);
      await agentsService.addInterview(complaintId, {
        ...form,
        interviewee_name: form.interviewee_name.trim(),
        interviewee_role: form.interviewee_role.trim(),
        transcript: form.transcript.trim()
      });
      setForm(INITIAL_FORM);
      toast.success('Entrevista registrada.');
      await loadInterviews();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar entrevista.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (interviewId) => {
    try {
      setRemovingId(interviewId);
      await agentsService.deleteInterview(complaintId, interviewId);
      toast.success('Entrevista removida.');
      await loadInterviews();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao remover entrevista.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entrevistas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">Entrevistado</label>
            <Input
              value={form.interviewee_name}
              onChange={(event) => handleChange('interviewee_name', event.target.value)}
              disabled={!canEdit || submitting}
              placeholder="Nome ou descrição"
            />
          </div>
          <div className="md:col-span-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">Cargo</label>
            <Input
              value={form.interviewee_role}
              onChange={(event) => handleChange('interviewee_role', event.target.value)}
              disabled={!canEdit || submitting}
              placeholder="Cargo ou área"
            />
          </div>
          <div className="md:col-span-1">
            <label className="mb-2 block text-sm font-medium text-gray-700">Data</label>
            <Input
              type="date"
              value={form.interview_date}
              onChange={(event) => handleChange('interview_date', event.target.value)}
              disabled={!canEdit || submitting}
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Transcrição</label>
          <Textarea
            rows={8}
            value={form.transcript}
            onChange={(event) => handleChange('transcript', event.target.value)}
            disabled={!canEdit || submitting}
            placeholder="Cole aqui a transcrição da entrevista."
          />
        </div>

        {canEdit ? (
          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Salvando...' : 'Adicionar Entrevista'}
            </Button>
          </div>
        ) : null}

        <div className="rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 px-4 py-3">
            <p className="text-sm font-medium text-gray-900">Entrevistas Registradas</p>
          </div>
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="px-4 py-4 text-sm text-gray-500">Carregando entrevistas...</div>
            ) : interviews.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500">Nenhuma entrevista registrada até agora.</div>
            ) : (
              interviews.map((interview) => (
                <div key={interview.id} className="flex items-center justify-between gap-4 px-4 py-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{interview.interviewee_name}</p>
                    <p className="text-sm text-gray-600">
                      {[interview.interviewee_role, interview.interview_date].filter(Boolean).join(' | ')}
                    </p>
                  </div>
                  {canEdit ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(interview.id)}
                      disabled={removingId === interview.id}
                    >
                      {removingId === interview.id ? 'Removendo...' : 'Remover'}
                    </Button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InterviewUpload;
